import { useState } from "react";
import { Reveal } from "@components/ui";

interface PolicySection {
  number: number;
  title: string;
  content: React.ReactNode;
}

function AccordionItem({
  section,
  isOpen,
  onToggle,
}: {
  section: PolicySection;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className={`rounded-2xl border transition-all duration-300 ${
        isOpen
          ? "bg-white border-[#122d63]/15 shadow-[0_2px_16px_rgba(18,45,99,0.06)]"
          : "bg-white border-[#e9e8e6] hover:border-[#d0d0d0] hover:shadow-sm"
      }`}
    >
      <button
        className="w-full flex items-center gap-4 md:gap-5 p-4 md:p-6 text-left cursor-pointer"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <div
          className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center shrink-0 text-[14px] md:text-[16px] font-bold transition-all duration-300 ${
            isOpen
              ? "bg-[#122d63] text-white"
              : "bg-[#f6f6f9] text-[#62636c]"
          }`}
        >
          {String(section.number).padStart(2, "0")}
        </div>
        <span
          className={`flex-1 text-[16px] md:text-[20px] lg:text-[24px] font-semibold tracking-[-0.04em] leading-[1.3] transition-colors duration-200 ${
            isOpen ? "text-[#122d63]" : "text-[#1e1f24]"
          }`}
        >
          {section.title}
        </span>
        <div
          className={`w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
            isOpen ? "bg-[#122d63] rotate-180" : "bg-[#f1f1f1]"
          }`}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            className="transition-colors duration-200"
          >
            <path
              d="M3.5 5.25L7 8.75L10.5 5.25"
              stroke={isOpen ? "white" : "#1e1f24"}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </button>

      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 md:px-6 pb-5 md:pb-6 pt-0">
            <div className="w-full h-px bg-[#f0efed] mb-5" />
            <div className="md:pl-[52px] lg:pl-[68px] text-[14px] md:text-[18px] lg:text-[20px] text-[#62636c] leading-[1.6] tracking-[-0.04em]">
              {section.content}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="w-2 h-2 min-w-2 rounded-full bg-[#122d63]/40 mt-2.5" />
      <span>{children}</span>
    </li>
  );
}

function PolicyTable({
  headers,
  rows,
}: {
  headers: string[];
  rows: string[][];
}) {
  return (
    <div className="overflow-x-auto w-full my-2">
      <table className="w-full rounded-xl border border-[#e9e8e6] overflow-hidden border-collapse">
        <thead>
          <tr className="bg-[#122d63] text-white">
            {headers.map((h, i) => (
              <th
                key={i}
                className={`py-3 px-4 text-left text-[14px] md:text-[16px] lg:text-[18px] font-semibold tracking-[-0.02em] ${
                  i === 0 ? "rounded-tl-xl" : ""
                } ${i === headers.length - 1 ? "rounded-tr-xl" : ""}`}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className={ri % 2 === 0 ? "bg-[#fafafa]" : "bg-white"}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className="py-3 px-4 border-t border-[#f0efed] text-[13px] md:text-[16px] lg:text-[18px] text-[#62636c] tracking-[-0.02em] leading-[1.5]"
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const policySections: PolicySection[] = [
  {
    number: 1,
    title: "Introduction",
    content: (
      <p>
        At Gas Integrated Solutions L.L.C. (GASIS), your privacy and data
        security are our top priorities. This Privacy Policy explains how we
        collect, use, store, and protect your personal information when you visit
        www.gasis.ae, use our mobile apps, or engage with our services. By using
        this website or any GASIS service, you consent to the practices described
        below.
      </p>
    ),
  },
  {
    number: 2,
    title: "Our Identity and Contact Details",
    content: (
      <div className="flex flex-col gap-2">
        <p>Gas Integrated Solutions L.L.C</p>
        <p>Trade License No.: 1234567 — Dubai Economic Department</p>
        <p>
          Registered Address: Dubai Investment Park 1, Dubai, United Arab
          Emirates
        </p>
        <p>Email: help@gasis.ae</p>
        <p>Hotline: 800-GASIS (42747)</p>
        <p>Data Protection Officer (DPO): privacy@gasis.ae</p>
      </div>
    ),
  },
  {
    number: 3,
    title: "Information We Collect",
    content: (
      <PolicyTable
        headers={["Category", "Examples of Data Collected", "Purpose of Collection"]}
        rows={[
          [
            "Personal Information",
            "Name, Emirates ID, address, email, mobile number",
            "Customer registration, billing, support",
          ],
          [
            "Account Data",
            "Meter number, unit location, billing history",
            "Meter reading and invoice management",
          ],
          [
            "Payment Data",
            "Card details (via secure gateway), transaction IDs",
            "Processing payments and refunds",
          ],
          [
            "Technical Data",
            "IP address, device type, browser, usage statistics",
            "Website analytics and security",
          ],
          [
            "Communication Records",
            "Emails, calls, WhatsApp messages with support",
            "Service quality and training",
          ],
          [
            "Cookies & Tracking",
            "Session cookies, analytics cookies",
            "Improve site experience and load performance",
          ],
        ]}
      />
    ),
  },
  {
    number: 4,
    title: "How We Use Your Information",
    content: (
      <div className="flex flex-col gap-5">
        <ul className="flex flex-col gap-3">
          <BulletItem>To create and manage customer accounts.</BulletItem>
          <BulletItem>To generate and deliver accurate bills.</BulletItem>
          <BulletItem>
            To communicate about payments, safety, or maintenance.
          </BulletItem>
          <BulletItem>To provide emergency and technical support.</BulletItem>
          <BulletItem>
            To comply with UAE tax and regulatory obligations.
          </BulletItem>
          <BulletItem>
            To analyze system performance and user behavior for service
            improvement.
          </BulletItem>
        </ul>
        <div className="bg-[#f6f6f9] rounded-xl px-5 md:px-8 py-4 border-l-4 border-[#122d63]">
          <p className="font-medium">
            We never sell or lease customer information to third parties.
          </p>
        </div>
      </div>
    ),
  },
  {
    number: 5,
    title: "Legal Basis for Processing",
    content: (
      <div className="flex flex-col gap-4">
        <p>
          Data processing is carried out under the following lawful bases:
        </p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            <strong>Contractual necessity</strong> — to deliver the gas supply
            and billing services you requested.
          </BulletItem>
          <BulletItem>
            <strong>Legal obligation</strong> — to comply with Civil Defense,
            ESMA, and FTA regulations.
          </BulletItem>
          <BulletItem>
            <strong>Legitimate interest</strong> — to improve safety and
            customer experience.
          </BulletItem>
          <BulletItem>
            <strong>Consent</strong> — for marketing communications or
            non-essential cookies.
          </BulletItem>
        </ul>
      </div>
    ),
  },
  {
    number: 6,
    title: "Data Retention",
    content: (
      <ul className="flex flex-col gap-3">
        <BulletItem>
          Billing and contract records are retained for a minimum of{" "}
          <strong>7 years</strong> as required by law.
        </BulletItem>
        <BulletItem>
          Support tickets and meter data are kept for operational reference for{" "}
          <strong>3 years</strong>.
        </BulletItem>
        <BulletItem>
          You may request deletion once your account is closed and legal
          retention periods expire.
        </BulletItem>
      </ul>
    ),
  },
  {
    number: 7,
    title: "Data Storage and Security",
    content: (
      <ul className="flex flex-col gap-3">
        <BulletItem>
          All data is stored on secure servers within the UAE.
        </BulletItem>
        <BulletItem>
          Encrypted (SSL/TLS 256-bit) connections protect all online
          transactions.
        </BulletItem>
        <BulletItem>
          GASIS follows <strong>ISO 27001 and PCI DSS</strong> compliance
          standards.
        </BulletItem>
        <BulletItem>
          Access to personal data is restricted to authorized employees only.
        </BulletItem>
        <BulletItem>
          Regular security audits and penetration tests are conducted.
        </BulletItem>
      </ul>
    ),
  },
  {
    number: 8,
    title: "Data Sharing and Disclosure",
    content: (
      <div className="flex flex-col gap-4">
        <p>We may share your data only with:</p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            Authorized government bodies (DEWA, Dubai Civil Defense, FTA).
          </BulletItem>
          <BulletItem>
            Contracted service providers bound by confidentiality agreements.
          </BulletItem>
          <BulletItem>
            Law enforcement agencies in response to official requests. All third
            parties are bound by confidentiality agreements and UAE data laws.
          </BulletItem>
        </ul>
      </div>
    ),
  },
  {
    number: 9,
    title: "Your Data Rights (Under UAE PDPL)",
    content: (
      <div className="flex flex-col gap-4">
        <p>You have the right to:</p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            Access your personal data and receive a copy.
          </BulletItem>
          <BulletItem>
            <strong>Rectify</strong> inaccurate information.
          </BulletItem>
          <BulletItem>
            <strong>Erase</strong> data when no longer required by law.
          </BulletItem>
          <BulletItem>
            <strong>Withdraw consent</strong> for marketing communications.
          </BulletItem>
          <BulletItem>
            <strong>Restrict processing</strong> in certain circumstances.
            Requests can be submitted to <strong>privacy@gasis.ae</strong>.
          </BulletItem>
        </ul>
        <p>
          GASIS will respond within 30 calendar days as per UAE PDPL
          requirements.
        </p>
      </div>
    ),
  },
  {
    number: 10,
    title: "Marketing and Communication Preferences",
    content: (
      <ul className="flex flex-col gap-3">
        <BulletItem>
          You can opt out of marketing emails via the unsubscribe link in any
          email.
        </BulletItem>
        <BulletItem>
          SMS and WhatsApp preferences can be updated via your account settings.
        </BulletItem>
        <BulletItem>
          Essential service notifications (billing, safety alerts) cannot be
          opted out of.
        </BulletItem>
      </ul>
    ),
  },
  {
    number: 11,
    title: "International Data Transfers",
    content: (
      <p>
        GASIS stores data locally in the UAE. If any transfer is required outside
        the UAE, such transfers will comply with Article 22 of the UAE PDPL, and
        the subject is guaranteed adequate safeguards.
      </p>
    ),
  },
  {
    number: 12,
    title: "Cookies Notice",
    content: (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-[#1e1f24]">What are Cookies?</p>
          <p>
            Cookies are small text files stored on your device to improve website
            functionality and analytics.
          </p>
        </div>
        <PolicyTable
          headers={["Type", "Purpose", "Example"]}
          rows={[
            [
              "Essential Cookies",
              "Enable core functions like log-in and payments",
              "Session ID cookies",
            ],
            [
              "Performance Cookies",
              "Collect anonymous usage data to enhance UX",
              "Google Analytics",
            ],
            [
              "Functional Cookies",
              "Remember preferences (language, theme)",
              "Preference cookies",
            ],
            [
              "Advertising Cookies",
              "Optional — measure campaign effectiveness",
              "Meta Pixel / Google Ads",
            ],
          ]}
        />
        <div className="flex flex-col gap-2">
          <p className="font-semibold text-[#1e1f24]">Managing Cookies:</p>
          <p>
            You can accept or reject non-essential cookies via our Cookie Consent
            banner or by changing browser settings. Rejecting cookies may limit
            certain site functions (e.g., auto login).
          </p>
        </div>
      </div>
    ),
  },
  {
    number: 13,
    title: "Children's Privacy",
    content: (
      <p>
        Our website and services are not intended for individuals aged 18 and
        below.
      </p>
    ),
  },
  {
    number: 14,
    title: "Data Breach Procedure",
    content: (
      <div className="flex flex-col gap-4">
        <p>If a data breach occurs:</p>
        <ul className="flex flex-col gap-3">
          <BulletItem>
            GASIS will immediately contain the incident and notify the Data
            Office within 72 hours.
          </BulletItem>
          <BulletItem>
            Affected customers will be informed if there is a risk to their
            rights or privacy.
          </BulletItem>
        </ul>
      </div>
    ),
  },
  {
    number: 15,
    title: "Links to Other Websites",
    content: (
      <p>
        Our site may contain links to third-party websites. GASIS is not
        responsible for their content or privacy practices and encourages users
        to review their own privacy policies.
      </p>
    ),
  },
  {
    number: 16,
    title: "Changes to This Policy",
    content: (
      <p>
        This Privacy Policy is reviewed annually. In the event of a material
        change, a full notice will be published at www.gasis.ae with a
        &apos;Last Updated&apos; date. Material changes will be communicated via
        email.
      </p>
    ),
  },
];

export default function PolicyContentSection() {
  const [openSections, setOpenSections] = useState<Set<number>>(
    new Set([1])
  );

  const toggleSection = (num: number) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(num)) {
        next.delete(num);
      } else {
        next.add(num);
      }
      return next;
    });
  };

  return (
    <section className="w-full bg-[#fefcfc] py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[900px] mx-auto flex flex-col gap-3 md:gap-4">
        {policySections.map((section) => (
          <Reveal key={section.number} direction="up" delay={section.number <= 3 ? section.number * 40 : 0}>
            <AccordionItem
              section={section}
              isOpen={openSections.has(section.number)}
              onToggle={() => toggleSection(section.number)}
            />
          </Reveal>
        ))}
      </div>
    </section>
  );
}
